import React from 'react';
import { productData } from '../../data/mockData';

// Mapping used to look up product images from category id → folder name
const CATEGORY_FOLDER_MAP = {
  'stickers': 'Stickers',
  'labels': 'Labels',
  'decals': 'Decals & Clings',
  'iron-ons': 'Wearables',
  'magnets': 'Unique Custom Products',
  'specialty': 'Specialty Niche Products',
};

const getProductImagePath = (categoryId, subcategory) => {
  const folder = CATEGORY_FOLDER_MAP[categoryId];
  if (!folder || !subcategory) return null;
  return `/selected-products/${folder}/${subcategory.name}.png`;
};

/**
 * Shown across Steps 3-6 of the Quote Calculator — displays the
 * already-chosen product (image + category + format + material) so the
 * customer always sees what they're configuring.
 *
 * iter114s.59 — Operator brief asked for the Selected Product block
 * to "visually align within the PAGE 2 • Size & Quantity header
 * region", with the text block and image scaled larger so the
 * hierarchy feels integrated rather than stacked. We now expose two
 * `variant`s:
 *   • `variant="header"` — renders ON the green gradient CardHeader,
 *     used on steps 4-6 so the selected product reads as part of the
 *     page heading. Image is enlarged (w-32 → w-40 on md+) and text
 *     scales up to match the CardTitle weight.
 *   • `variant="inline"` (default) — the previous in-body green panel,
 *     still used on step 3 where the customer is mid-selection and
 *     needs the affordance below the header (the header is showing
 *     "Choose a Material" cues, not summarizing the selection).
 */
export const SelectedProductDisplay = ({
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  step,
  materials = [],
  onImageClick,
  variant = 'inline',
}) => {
  if (step < 3 || !selectedProduct || !selectedFormat) return null;

  const category = productData.categories.find((c) => c.id === selectedProduct);
  const subcategory = category?.subcategories.find((s) => s.id === selectedFormat);
  const activeImage = getProductImagePath(selectedProduct, subcategory);
  const material = materials.find((m) => m.id === selectedMaterial);

  // ============================ HEADER VARIANT ============================
  // Renders directly under the page title on the green gradient header.
  // Image bumped to a flat 96/128px square, text bumped to base/lg with
  // white-on-green colourway so it reads as a continuation of the
  // CardHeader typography rather than a separate inner panel.
  if (variant === 'header') {
    return (
      <div
        className="flex items-center gap-3 md:gap-4"
        data-testid="selected-product-display-header"
      >
        {activeImage && (
          <button
            type="button"
            onClick={() => onImageClick?.(activeImage)}
            className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white p-1.5 ring-2 ring-white/70 hover:ring-white shadow-sm transition-all hover:scale-105"
            data-testid="selected-product-image"
            aria-label="Open enlarged product image"
          >
            <img
              src={activeImage}
              alt={subcategory?.name}
              className="w-full h-full object-contain"
            />
          </button>
        )}
        <div className="flex-1 min-w-0 text-white">
          <p className="text-[10px] uppercase tracking-wide text-white/70 font-semibold">
            Selected Product
          </p>
          <h3 className="text-sm md:text-base font-bold leading-tight truncate">
            {category?.name || ''}{' '}
            <span className="font-medium text-white/90">{subcategory?.name || ''}</span>
          </h3>
          {selectedMaterial && step >= 4 && (
            <p className="text-xs md:text-sm text-white/85 mt-0.5 truncate">
              <span className="text-white/60">Material:</span>{' '}
              {material?.name || selectedMaterial}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ============================ INLINE VARIANT (default) =================
  // Original in-body panel — kept for step 3 where the body is the
  // active selection surface and the user benefits from a high-contrast
  // affordance showing what they've already configured.
  return (
    <div
      className="mb-6 p-6 bg-green-50 border-2 border-[#4A7C59] rounded-lg"
      data-testid="selected-product-display"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        {activeImage && (
          <div className="flex-shrink-0">
            <img
              src={activeImage}
              alt={subcategory?.name}
              onClick={() => onImageClick?.(activeImage)}
              className="w-64 h-64 object-contain rounded-lg border-2 border-[#4A7C59] bg-white p-2 cursor-pointer hover:border-[#6FBF73] transition-all hover:scale-105"
              title="Click to enlarge"
              data-testid="selected-product-image"
            />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">Selected Product:</p>
          <h3 className="text-xl font-bold text-[#4A7C59]">{category?.name || ''}</h3>
          <p className="text-lg font-semibold text-gray-800">{subcategory?.name || ''}</p>
          {selectedMaterial && step >= 4 && (
            <p className="text-sm text-gray-600 mt-1">
              Material: {material?.name || selectedMaterial}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
